import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, CreditCard as Edit, Trash2, Save, RefreshCw, Cpu, AlertCircle, ArrowLeft, BarChart3, Sliders, DollarSign, Download, AlertTriangle, CheckCircle, XCircle, Bell } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Modal } from '@shared/components/ui/Modal';
import { ParameterSlider } from '@shared/components/ui/ParameterSlider';
import { ParameterPresetSelector } from '../../components/ParameterPresetSelector';
import {
  LLMParameters,
  PARAMETER_CONFIGS,
  DEFAULT_PARAMETERS,
  mergeWithDefaults,
  estimateTokenCost,
  validateParameters,
  formatParameters
} from '../../utils/llmParameterHelpers';

interface AvailableModel {
  id: string;
  name: string;
  description: string | null;
  context_length: number;
  is_available: boolean;
  last_synced: string;
}

interface AdminAlert {
  id: string;
  alert_type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  metadata: any;
  resolved: boolean;
  created_at: string;
}

interface LLMConfiguration {
  id: string;
  prompt_id: string;
  llm_name: string;
  parameters: any;
  is_default: boolean;
  api_key: string | null;
  created_at: string;
  updated_at: string;
  prompts?: {
    name: string;
  };
}

const AdminLLM: React.FC = () => {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState<LLMConfiguration[]>([]);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LLMConfiguration | null>(null);
  const [formData, setFormData] = useState({
    prompt_id: '',
    llm_name: '',
    parameters: '{}',
    is_default: false
  });
  const [useSliders, setUseSliders] = useState(true);
  const [sliderParameters, setSliderParameters] = useState<LLMParameters>(DEFAULT_PARAMETERS);
  const [estimatedCost, setEstimatedCost] = useState({ estimatedInputCost: 0, estimatedOutputCost: 0, estimatedTotalCost: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [configsResult, modelsResult, promptsResult, alertsResult] = await Promise.all([
        supabase
          .from('llm_configurations')
          .select(`
            *,
            prompts (
              name
            )
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('available_llm_models')
          .select('*')
          .eq('is_available', true)
          .order('name', { ascending: true }),
        supabase
          .from('prompts')
          .select('id, name')
          .order('name', { ascending: true }),
        supabase
          .from('admin_alerts')
          .select('*')
          .eq('resolved', false)
          .order('created_at', { ascending: false })
      ]);

      if (configsResult.error) throw configsResult.error;
      if (modelsResult.error) throw modelsResult.error;
      if (promptsResult.error) throw promptsResult.error;
      if (alertsResult.error) throw alertsResult.error;

      setConfigurations(configsResult.data || []);
      setAvailableModels(modelsResult.data || []);
      setPrompts(promptsResult.data || []);
      setAlerts(alertsResult.data || []);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Failed to load LLM configurations');
    } finally {
      setLoading(false);
    }
  };

  const syncModels = async () => {
    try {
      setSyncing(true);
      toast.info('Syncing models from OpenRouter...');

      const { data, error } = await supabase.functions.invoke('sync-llm-models');

      if (error) throw error;

      toast.success(`Successfully synced ${data.summary?.valid_models_processed || 0} models`);

      if (data.replacements?.length > 0) {
        toast.success(`Auto-fixed ${data.replacements.length} configuration(s) with unavailable models`);
      }

      if (data.unresolved?.length > 0) {
        toast.error(`${data.unresolved.length} configuration(s) have no available replacement — check alerts`);
      }

      setLastSyncTime(new Date().toISOString());
      loadData();
    } catch (error: any) {
      console.error('Error syncing models:', error);
      toast.error('Failed to sync models');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateNew = () => {
    setEditingConfig(null);
    const defaultParams = DEFAULT_PARAMETERS;
    setFormData({
      prompt_id: '',
      llm_name: '',
      parameters: JSON.stringify(defaultParams, null, 2),
      is_default: false
    });
    setSliderParameters(defaultParams);
    setUseSliders(true);
    updateCostEstimate('', defaultParams.max_tokens);
    setShowModal(true);
  };

  const handleEdit = (config: LLMConfiguration) => {
    setEditingConfig(config);
    const params = mergeWithDefaults(config.parameters);
    setFormData({
      prompt_id: config.prompt_id,
      llm_name: config.llm_name,
      parameters: JSON.stringify(params, null, 2),
      is_default: config.is_default
    });
    setSliderParameters(params);
    setUseSliders(true);
    updateCostEstimate(config.llm_name, params.max_tokens);
    setShowModal(true);
  };

  const updateCostEstimate = (model: string, maxTokens: number) => {
    if (model) {
      const cost = estimateTokenCost(maxTokens, model);
      setEstimatedCost(cost);
    }
  };

  const handleParameterChange = (param: keyof LLMParameters, value: number) => {
    const newParams = { ...sliderParameters, [param]: value };
    setSliderParameters(newParams);
    setFormData({ ...formData, parameters: JSON.stringify(newParams, null, 2) });

    if (param === 'max_tokens') {
      updateCostEstimate(formData.llm_name, value);
    }
  };

  const handlePresetSelect = (parameters: LLMParameters) => {
    setSliderParameters(parameters);
    setFormData({ ...formData, parameters: JSON.stringify(parameters, null, 2) });
    updateCostEstimate(formData.llm_name, parameters.max_tokens);
  };

  const handleModelChange = (modelId: string) => {
    setFormData({ ...formData, llm_name: modelId });
    updateCostEstimate(modelId, sliderParameters.max_tokens);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.prompt_id || !formData.llm_name) {
      toast.error('Prompt and model are required');
      return;
    }

    try {
      setSaving(true);

      let parameters = {};
      try {
        parameters = useSliders ? sliderParameters : JSON.parse(formData.parameters);
      } catch {
        toast.error('Invalid JSON format for parameters');
        return;
      }

      // Validate parameters
      const validation = validateParameters(parameters);
      if (!validation.valid) {
        toast.error(`Invalid parameters: ${validation.errors.join(', ')}`);
        return;
      }

      const configData = {
        prompt_id: formData.prompt_id,
        llm_name: formData.llm_name,
        parameters,
        is_default: formData.is_default,
        updated_at: new Date().toISOString()
      };

      if (editingConfig) {
        const { error } = await supabase
          .from('llm_configurations')
          .update(configData)
          .eq('id', editingConfig.id);

        if (error) throw error;
        toast.success('Configuration updated successfully');
      } else {
        const { error } = await supabase
          .from('llm_configurations')
          .insert({
            ...configData,
            created_at: new Date().toISOString()
          });

        if (error) throw error;
        toast.success('Configuration created successfully');
      }

      setShowModal(false);
      loadData();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('llm_configurations')
        .delete()
        .eq('id', configId);

      if (error) throw error;

      toast.success('Configuration deleted successfully');
      loadData();
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      toast.error('Failed to delete configuration');
    }
  };

  const handleExportLLMConfig = async () => {
    try {
      const { exportLLMConfiguration } = await import('../../utils/configurationExporter');
      const result = await exportLLMConfiguration();

      if (result.success) {
        toast.success(`Exported ${result.filename} successfully!`);
      } else {
        toast.error(result.error || 'Failed to export LLM configurations');
      }
    } catch (error: any) {
      console.error('Error exporting:', error);
      toast.error('Failed to export LLM configurations');
    }
  };

  const handleExportParameters = async () => {
    try {
      const { exportParametersDocumentation } = await import('../../utils/configurationExporter');
      const result = await exportParametersDocumentation();

      if (result.success) {
        toast.success(`Exported ${result.filename} successfully!`);
      } else {
        toast.error(result.error || 'Failed to export parameters documentation');
      }
    } catch (error: any) {
      console.error('Error exporting:', error);
      toast.error('Failed to export parameters documentation');
    }
  };

  const resolveAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('admin_alerts')
        .update({ resolved: true, resolved_at: new Date().toISOString() })
        .eq('id', alertId);

      if (error) throw error;

      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert resolved');
    } catch (error: any) {
      console.error('Error resolving alert:', error);
      toast.error('Failed to resolve alert');
    }
  };

  const getModelStatus = (llmName: string): { status: 'available' | 'unavailable' | 'unknown'; label: string } => {
    if (!availableModels.length) return { status: 'unknown', label: 'Unknown' };
    const found = availableModels.find(m => m.id.toLowerCase() === llmName.toLowerCase());
    return found
      ? { status: 'available', label: 'Available' }
      : { status: 'unavailable', label: 'Unavailable' };
  };

  const unresolvedAlertCount = alerts.length;

  const filteredConfigs = configurations.filter(config =>
    config.prompts?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.llm_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">LLM Configuration</h1>
            <p className="text-gray-600">Manage language model configurations and availability</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/admin/llm/optimization')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Sliders className="w-4 h-4" />
              Optimize
            </Button>
            <Button
              onClick={() => navigate('/admin/llm/performance')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Performance
            </Button>
            <Button
              onClick={syncModels}
              disabled={syncing}
              variant="outline"
              className="flex items-center gap-2 relative"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Models'}
              {unresolvedAlertCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {unresolvedAlertCount}
                </span>
              )}
            </Button>
            <Button
              onClick={handleExportParameters}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Parameters
            </Button>
            <Button
              onClick={handleExportLLMConfig}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Config
            </Button>
            <Button onClick={handleCreateNew} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Configuration
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Available Models</h3>
            <Cpu className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{availableModels.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Configurations</h3>
            <Cpu className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{configurations.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Default Configs</h3>
            <Cpu className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {configurations.filter(c => c.is_default).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Active Alerts</h3>
            <Bell className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{unresolvedAlertCount}</p>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-red-500">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">Model Health Alerts ({alerts.length})</h2>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`flex items-start justify-between p-3 rounded-lg border ${
                  alert.severity === 'critical'
                    ? 'bg-red-50 border-red-200'
                    : alert.severity === 'warning'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {alert.severity === 'critical' ? (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    ) : alert.severity === 'warning' ? (
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    ) : (
                      <Bell className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-gray-900">{alert.title}</span>
                  </div>
                  <p className="text-xs text-gray-600 ml-6">{alert.message}</p>
                  <p className="text-xs text-gray-400 ml-6 mt-1">
                    {new Date(alert.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => resolveAlert(alert.id)}
                  className="ml-3 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 flex-shrink-0"
                >
                  <CheckCircle className="w-3 h-3" />
                  Resolve
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search configurations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Configurations</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prompt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredConfigs.map((config) => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {config.prompts?.name || 'Unknown'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">{config.llm_name}</span>
                      {(() => {
                        const modelStatus = getModelStatus(config.llm_name);
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                              modelStatus.status === 'available'
                                ? 'bg-green-100 text-green-700'
                                : modelStatus.status === 'unavailable'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                            title={modelStatus.label}
                          >
                            {modelStatus.status === 'available' ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : modelStatus.status === 'unavailable' ? (
                              <XCircle className="w-3 h-3" />
                            ) : (
                              <AlertCircle className="w-3 h-3" />
                            )}
                            {modelStatus.label}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        config.is_default
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {config.is_default ? 'Default' : 'Secondary'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(config.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(config)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredConfigs.length === 0 && (
            <div className="text-center py-12">
              <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No configurations found</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Available Models ({availableModels.length})</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableModels.map((model) => (
              <div key={model.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors">
                <h3 className="font-medium text-gray-900 mb-1">{model.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{model.id}</p>
                {model.description && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{model.description}</p>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Context: {model.context_length?.toLocaleString()}</span>
                  <span className="text-green-600">Available</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingConfig ? 'Edit Configuration' : 'Create Configuration'}
        size="lg"
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="prompt_id" className="block text-sm font-medium text-gray-700 mb-1">
              Prompt *
            </label>
            <select
              id="prompt_id"
              value={formData.prompt_id}
              onChange={(e) => setFormData({ ...formData, prompt_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Select a prompt...</option>
              {prompts.map((prompt) => (
                <option key={prompt.id} value={prompt.id}>
                  {prompt.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="llm_name" className="block text-sm font-medium text-gray-700 mb-1">
              Model *
            </label>
            <select
              id="llm_name"
              value={formData.llm_name}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Select a model...</option>
              {availableModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.id})
                </option>
              ))}
            </select>
          </div>

          {/* Toggle between sliders and JSON */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sliders className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Configure Parameters</span>
            </div>
            <button
              type="button"
              onClick={() => setUseSliders(!useSliders)}
              className="text-xs text-blue-600 hover:text-blue-700 underline"
            >
              {useSliders ? 'Switch to JSON' : 'Switch to Sliders'}
            </button>
          </div>

          {useSliders ? (
            <div className="space-y-6">
              {/* Parameter Presets */}
              <ParameterPresetSelector
                onSelectPreset={handlePresetSelect}
                currentParameters={sliderParameters}
              />

              {/* Cost Estimation */}
              {formData.llm_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Estimated Cost per Call</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <div className="text-gray-600">Input</div>
                      <div className="font-semibold text-blue-900">${estimatedCost.estimatedInputCost.toFixed(6)}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Output</div>
                      <div className="font-semibold text-blue-900">${estimatedCost.estimatedOutputCost.toFixed(6)}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Total</div>
                      <div className="font-semibold text-blue-900">${estimatedCost.estimatedTotalCost.toFixed(6)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Parameter Sliders */}
              <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                {PARAMETER_CONFIGS.map((config) => (
                  <ParameterSlider
                    key={config.name}
                    label={config.label}
                    value={sliderParameters[config.name]}
                    onChange={(value) => handleParameterChange(config.name, value)}
                    min={config.min}
                    max={config.max}
                    step={config.step}
                    description={config.description}
                    recommendedMin={config.recommendedMin}
                    recommendedMax={config.recommendedMax}
                    unit={config.unit}
                  />
                ))}
              </div>

              {/* Parameter Summary */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 mb-1">Parameter Summary:</div>
                <div className="text-sm text-gray-900 font-mono">
                  {formatParameters(sliderParameters)}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="parameters" className="block text-sm font-medium text-gray-700 mb-1">
                Parameters (JSON)
              </label>
              <textarea
                id="parameters"
                value={formData.parameters}
                onChange={(e) => setFormData({ ...formData, parameters: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                rows={6}
                placeholder='{"temperature": 0.7, "max_tokens": 4000}'
              />
            </div>
          )}

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">Set as default configuration</span>
            </label>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : editingConfig ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminLLM;
